/**
 * DNA CRM - Gestão de Leads e Pipeline de Vendas
 * Parte do DNA Growth Engine
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import api from '../../lib/api';
import { 
  Users, Phone, Mail, MessageSquare, Calendar, Plus, Search, Filter,
  ArrowRight, CheckCircle, XCircle, Clock, Target, TrendingUp,
  MoreVertical, Edit, Trash2, Eye, Send, UserPlus, Building,
  GraduationCap, Baby, Briefcase, ChevronRight, RefreshCw
} from 'lucide-react';

const STATUS_CONFIG = {
  novo: { label: 'Novo Lead', color: 'bg-blue-500', icon: UserPlus },
  contato: { label: 'Primeiro Contato', color: 'bg-indigo-500', icon: Phone },
  conversa: { label: 'Em Conversa', color: 'bg-purple-500', icon: MessageSquare },
  visita: { label: 'Visita Agendada', color: 'bg-amber-500', icon: Calendar },
  proposta: { label: 'Proposta Enviada', color: 'bg-orange-500', icon: Send },
  decisao: { label: 'Em Decisão', color: 'bg-pink-500', icon: Clock },
  matricula: { label: 'Matrícula', color: 'bg-green-500', icon: CheckCircle },
  perdido: { label: 'Perdido', color: 'bg-red-500', icon: XCircle }
};

const ORIGEM_CONFIG = {
  instagram: { label: 'Instagram', icon: '📸' },
  facebook: { label: 'Facebook', icon: '👤' },
  whatsapp: { label: 'WhatsApp', icon: '💬' },
  site: { label: 'Site', icon: '🌐' },
  indicacao: { label: 'Indicação', icon: '👥' },
  campanha: { label: 'Campanha', icon: '📢' },
  evento: { label: 'Evento', icon: '🎉' },
  telefone: { label: 'Telefone', icon: '📞' },
  presencial: { label: 'Presencial', icon: '🏫' },
  outro: { label: 'Outro', icon: '📋' }
};

const INTERESSE_CONFIG = {
  educacao_infantil: { label: 'Educação Infantil', icon: Baby },
  fundamental_1: { label: 'Fundamental I', icon: GraduationCap },
  fundamental_2: { label: 'Fundamental II', icon: GraduationCap },
  ensino_medio: { label: 'Ensino Médio', icon: GraduationCap },
  periodo_integral: { label: 'Período Integral', icon: Clock },
  nao_definido: { label: 'Não Definido', icon: Target }
};

export default function DnaCRM() {
  const [leads, setLeads] = useState([]);
  const [pipeline, setPipeline] = useState({});
  const [estatisticas, setEstatisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('pipeline'); // pipeline, lista
  const [filtros, setFiltros] = useState({ status: '', origem: '', busca: '' });
  const [showNovoLead, setShowNovoLead] = useState(false);
  const [showDetalheLead, setShowDetalheLead] = useState(false);
  const [leadSelecionado, setLeadSelecionado] = useState(null);
  const [novoLead, setNovoLead] = useState({
    nome: '', telefone: '', whatsapp: '', email: '',
    origem: 'outro', interesse: 'nao_definido',
    nome_crianca: '', idade_crianca: '', serie_interesse: '', observacoes: ''
  });

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      const [pipelineRes, statsRes] = await Promise.all([
        api.get('/growth/crm/leads/pipeline'),
        api.get('/growth/crm/estatisticas?periodo=mes')
      ]);
      setPipeline(pipelineRes.data);
      setEstatisticas(statsRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do CRM');
    } finally {
      setLoading(false);
    }
  }, []);

  const carregarLeadsLista = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filtros.status) params.append('status', filtros.status);
      if (filtros.origem) params.append('origem', filtros.origem);
      if (filtros.busca) params.append('busca', filtros.busca);
      
      const res = await api.get(`/growth/crm/leads?${params.toString()}`);
      setLeads(res.data.leads);
    } catch (error) {
      console.error('Erro ao carregar leads:', error);
    }
  }, [filtros]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  useEffect(() => {
    if (viewMode === 'lista') {
      carregarLeadsLista();
    }
  }, [viewMode, carregarLeadsLista]);

  const handleCriarLead = async () => {
    try {
      if (!novoLead.nome) {
        toast.error('Nome é obrigatório');
        return;
      }
      
      await api.post('/growth/crm/leads', novoLead);
      toast.success('Lead criado com sucesso!');
      setShowNovoLead(false);
      setNovoLead({
        nome: '', telefone: '', whatsapp: '', email: '',
        origem: 'outro', interesse: 'nao_definido',
        nome_crianca: '', idade_crianca: '', serie_interesse: '', observacoes: ''
      });
      carregarDados();
    } catch (error) {
      console.error('Erro ao criar lead:', error);
      toast.error('Erro ao criar lead');
    }
  };

  const handleMoverLead = async (leadId, novoStatus) => {
    try {
      await api.post(`/growth/crm/leads/${leadId}/mover?novo_status=${novoStatus}`);
      toast.success(`Lead movido para ${STATUS_CONFIG[novoStatus].label}`);
      carregarDados();
    } catch (error) {
      console.error('Erro ao mover lead:', error);
      toast.error('Erro ao mover lead');
    }
  };

  const handleVerDetalhes = async (leadId) => {
    try {
      const res = await api.get(`/growth/crm/leads/${leadId}`);
      setLeadSelecionado(res.data);
      setShowDetalheLead(true);
    } catch (error) {
      console.error('Erro ao carregar detalhes:', error);
      toast.error('Erro ao carregar detalhes do lead');
    }
  };

  const handleAdicionarInteracao = async (tipo, conteudo) => {
    if (!leadSelecionado || !conteudo) return;
    
    try {
      await api.post(`/growth/crm/leads/${leadSelecionado.id}/interacoes`, {
        tipo, conteudo, automatica: false
      });
      
      // Recarregar lead
      const res = await api.get(`/growth/crm/leads/${leadSelecionado.id}`);
      setLeadSelecionado(res.data);
      toast.success('Interação registrada');
    } catch (error) {
      console.error('Erro ao adicionar interação:', error);
      toast.error('Erro ao registrar interação');
    }
  };

  // Card de estatísticas no topo
  const StatCard = ({ title, value, icon: Icon, color, subtext }) => (
    <Card className="bg-white border-0 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
          </div>
          <div className={`p-3 rounded-full ${color.replace('text-', 'bg-').replace('600', '100')}`}>
            <Icon className={`w-6 h-6 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Card de lead no pipeline
  const LeadCard = ({ lead }) => {
    const origemConfig = ORIGEM_CONFIG[lead.origem] || ORIGEM_CONFIG.outro;
    const interesseConfig = INTERESSE_CONFIG[lead.interesse] || INTERESSE_CONFIG.nao_definido;
    
    return (
      <div 
        className="bg-white p-3 rounded-lg shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => handleVerDetalhes(lead.id)}
        data-testid={`lead-card-${lead.id}`}
      >
        <div className="flex items-start justify-between mb-2">
          <h4 className="font-medium text-gray-800 text-sm truncate flex-1">{lead.nome}</h4>
          <span className="text-lg ml-2">{origemConfig.icon}</span>
        </div>
        
        {lead.telefone && (
          <p className="text-xs text-gray-500 flex items-center gap-1 mb-1">
            <Phone className="w-3 h-3" /> {lead.telefone}
          </p>
        )}
        
        <div className="flex items-center justify-between mt-2">
          <Badge variant="outline" className="text-xs">
            {interesseConfig.label}
          </Badge>
          {lead.valor_proposta && (
            <span className="text-xs font-medium text-green-600">
              R$ {lead.valor_proposta.toLocaleString('pt-BR')}
            </span>
          )}
        </div>
      </div>
    );
  };

  // Coluna do Pipeline
  const PipelineColumn = ({ status, data }) => {
    const config = STATUS_CONFIG[status];
    const Icon = config.icon;
    
    return (
      <div className="flex-1 min-w-[280px] max-w-[320px]">
        <div className={`${config.color} text-white p-3 rounded-t-lg flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4" />
            <span className="font-medium text-sm">{config.label}</span>
          </div>
          <Badge variant="secondary" className="bg-white/20 text-white text-xs">
            {data.count}
          </Badge>
        </div>
        
        <div className="bg-gray-50 p-2 rounded-b-lg min-h-[400px] max-h-[60vh] overflow-y-auto space-y-2">
          {data.leads?.map(lead => (
            <LeadCard key={lead.id} lead={lead} />
          ))}
          
          {data.leads?.length === 0 && (
            <p className="text-center text-gray-400 text-sm py-8">
              Nenhum lead nesta etapa
            </p>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Target className="w-7 h-7 text-blue-600" />
            DNA CRM
          </h1>
          <p className="text-gray-500 text-sm">Gestão de Leads e Pipeline de Vendas</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setViewMode(viewMode === 'pipeline' ? 'lista' : 'pipeline')}
          >
            {viewMode === 'pipeline' ? 'Ver Lista' : 'Ver Pipeline'}
          </Button>
          
          <Button onClick={() => setShowNovoLead(true)} data-testid="btn-novo-lead">
            <Plus className="w-4 h-4 mr-2" />
            Novo Lead
          </Button>
        </div>
      </div>

      {/* Estatísticas */}
      {estatisticas && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard 
            title="Total de Leads" 
            value={estatisticas.total_leads} 
            icon={Users} 
            color="text-blue-600"
            subtext={`${estatisticas.novos_periodo || 0} novos este mês`}
          />
          <StatCard 
            title="Matrículas" 
            value={estatisticas.matriculas} 
            icon={CheckCircle} 
            color="text-green-600"
            subtext={`${estatisticas.taxa_conversao}% conversão`}
          />
          <StatCard 
            title="Em Andamento" 
            value={estatisticas.total_leads - (estatisticas.matriculas || 0) - (estatisticas.perdidos || 0)} 
            icon={TrendingUp} 
            color="text-amber-600"
          />
          <StatCard 
            title="Valor em Propostas" 
            value={`R$ ${(estatisticas.valor_propostas || 0).toLocaleString('pt-BR')}`} 
            icon={Briefcase} 
            color="text-purple-600"
          />
        </div>
      )}

      {/* Pipeline View */}
      {viewMode === 'pipeline' && (
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-view">
          {['novo', 'contato', 'conversa', 'visita', 'proposta', 'decisao', 'matricula'].map(status => (
            <PipelineColumn 
              key={status} 
              status={status} 
              data={pipeline[status] || { count: 0, leads: [] }} 
            />
          ))}
        </div>
      )}

      {/* Lista View */}
      {viewMode === 'lista' && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input 
                  placeholder="Buscar leads..."
                  className="pl-10"
                  value={filtros.busca}
                  onChange={(e) => setFiltros(prev => ({ ...prev, busca: e.target.value }))}
                />
              </div>
              
              <Select value={filtros.status} onValueChange={(v) => setFiltros(prev => ({ ...prev, status: v }))}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filtros.origem} onValueChange={(v) => setFiltros(prev => ({ ...prev, origem: v }))}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Origem" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {Object.entries(ORIGEM_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.icon} {config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button variant="outline" size="icon" onClick={carregarLeadsLista}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {leads.map(lead => {
                const statusConfig = STATUS_CONFIG[lead.status];
                const origemConfig = ORIGEM_CONFIG[lead.origem] || ORIGEM_CONFIG.outro;
                
                return (
                  <div 
                    key={lead.id}
                    className="flex items-center gap-4 p-3 bg-white rounded-lg border hover:shadow-sm cursor-pointer"
                    onClick={() => handleVerDetalhes(lead.id)}
                  >
                    <div className={`w-2 h-12 rounded-full ${statusConfig.color}`} />
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800">{lead.nome}</h4>
                      <p className="text-sm text-gray-500">{lead.telefone || lead.email}</p>
                    </div>
                    <Badge className={`${statusConfig.color} text-white`}>
                      {statusConfig.label}
                    </Badge>
                    <span className="text-lg">{origemConfig.icon}</span>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                );
              })}
              
              {leads.length === 0 && (
                <p className="text-center text-gray-400 py-8">
                  Nenhum lead encontrado
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal Novo Lead */}
      <Dialog open={showNovoLead} onOpenChange={setShowNovoLead}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Novo Lead
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nome do Responsável *</Label>
                <Input 
                  value={novoLead.nome}
                  onChange={(e) => setNovoLead(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Nome completo"
                />
              </div>
              
              <div>
                <Label>Telefone</Label>
                <Input 
                  value={novoLead.telefone}
                  onChange={(e) => setNovoLead(prev => ({ ...prev, telefone: e.target.value }))}
                  placeholder="(11) 99999-9999"
                />
              </div>
              
              <div>
                <Label>WhatsApp</Label>
                <Input 
                  value={novoLead.whatsapp}
                  onChange={(e) => setNovoLead(prev => ({ ...prev, whatsapp: e.target.value }))}
                  placeholder="(11) 99999-9999"
                />
              </div>
              
              <div className="col-span-2">
                <Label>E-mail</Label>
                <Input 
                  type="email"
                  value={novoLead.email}
                  onChange={(e) => setNovoLead(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@exemplo.com"
                />
              </div>
              
              <div>
                <Label>Origem</Label>
                <Select 
                  value={novoLead.origem} 
                  onValueChange={(v) => setNovoLead(prev => ({ ...prev, origem: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ORIGEM_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        {config.icon} {config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Interesse</Label>
                <Select 
                  value={novoLead.interesse} 
                  onValueChange={(v) => setNovoLead(prev => ({ ...prev, interesse: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(INTERESSE_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Nome da Criança</Label>
                <Input 
                  value={novoLead.nome_crianca}
                  onChange={(e) => setNovoLead(prev => ({ ...prev, nome_crianca: e.target.value }))}
                  placeholder="Nome da criança"
                />
              </div>
              
              <div>
                <Label>Série de Interesse</Label>
                <Input 
                  value={novoLead.serie_interesse}
                  onChange={(e) => setNovoLead(prev => ({ ...prev, serie_interesse: e.target.value }))}
                  placeholder="Ex: 1º Ano"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Observações</Label>
                <Textarea 
                  value={novoLead.observacoes}
                  onChange={(e) => setNovoLead(prev => ({ ...prev, observacoes: e.target.value }))}
                  placeholder="Informações adicionais..."
                  rows={3}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovoLead(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCriarLead}>
              <Plus className="w-4 h-4 mr-2" />
              Criar Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Detalhes do Lead */}
      <Dialog open={showDetalheLead} onOpenChange={setShowDetalheLead}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {leadSelecionado && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>{leadSelecionado.nome}</span>
                  <Badge className={`${STATUS_CONFIG[leadSelecionado.status]?.color} text-white`}>
                    {STATUS_CONFIG[leadSelecionado.status]?.label}
                  </Badge>
                </DialogTitle>
              </DialogHeader>
              
              <Tabs defaultValue="info">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="info">Informações</TabsTrigger>
                  <TabsTrigger value="historico">Histórico</TabsTrigger>
                  <TabsTrigger value="acoes">Ações</TabsTrigger>
                </TabsList>
                
                <TabsContent value="info" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-500">Telefone</Label>
                      <p className="font-medium">{leadSelecionado.telefone || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">WhatsApp</Label>
                      <p className="font-medium">{leadSelecionado.whatsapp || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">E-mail</Label>
                      <p className="font-medium">{leadSelecionado.email || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Origem</Label>
                      <p className="font-medium">
                        {ORIGEM_CONFIG[leadSelecionado.origem]?.icon} {ORIGEM_CONFIG[leadSelecionado.origem]?.label}
                      </p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Interesse</Label>
                      <p className="font-medium">{INTERESSE_CONFIG[leadSelecionado.interesse]?.label}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Nome da Criança</Label>
                      <p className="font-medium">{leadSelecionado.nome_crianca || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Série de Interesse</Label>
                      <p className="font-medium">{leadSelecionado.serie_interesse || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Valor Proposta</Label>
                      <p className="font-medium text-green-600">
                        {leadSelecionado.valor_proposta 
                          ? `R$ ${leadSelecionado.valor_proposta.toLocaleString('pt-BR')}`
                          : '-'}
                      </p>
                    </div>
                  </div>
                  
                  {leadSelecionado.observacoes && (
                    <div>
                      <Label className="text-gray-500">Observações</Label>
                      <p className="text-sm bg-gray-50 p-3 rounded-lg">{leadSelecionado.observacoes}</p>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="historico">
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {leadSelecionado.interacoes?.map((interacao, idx) => (
                      <div key={idx} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="w-2 h-2 mt-2 rounded-full bg-blue-500" />
                        <div className="flex-1">
                          <p className="text-sm">{interacao.conteudo}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(interacao.created_at).toLocaleString('pt-BR')}
                            {interacao.automatica && ' • Automático'}
                          </p>
                        </div>
                      </div>
                    ))}
                    
                    {(!leadSelecionado.interacoes || leadSelecionado.interacoes.length === 0) && (
                      <p className="text-center text-gray-400 py-8">Nenhuma interação registrada</p>
                    )}
                  </div>
                </TabsContent>
                
                <TabsContent value="acoes">
                  <div className="space-y-4">
                    <div>
                      <Label>Mover para</Label>
                      <div className="grid grid-cols-4 gap-2 mt-2">
                        {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                          <Button
                            key={status}
                            variant={leadSelecionado.status === status ? "default" : "outline"}
                            size="sm"
                            className={leadSelecionado.status === status ? config.color : ''}
                            onClick={() => {
                              handleMoverLead(leadSelecionado.id, status);
                              setShowDetalheLead(false);
                            }}
                          >
                            {config.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <Label>Adicionar Nota</Label>
                      <div className="flex gap-2 mt-2">
                        <Input 
                          placeholder="Escreva uma nota..."
                          id="nova-nota"
                        />
                        <Button onClick={() => {
                          const input = document.getElementById('nova-nota');
                          if (input.value) {
                            handleAdicionarInteracao('nota', input.value);
                            input.value = '';
                          }
                        }}>
                          Adicionar
                        </Button>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button variant="outline" className="flex-1">
                        <Phone className="w-4 h-4 mr-2" />
                        Registrar Ligação
                      </Button>
                      <Button variant="outline" className="flex-1">
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Enviar WhatsApp
                      </Button>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
